import React, { useState, useEffect, useRef } from 'react'
import {
  Card, Table, Tag, Typography, theme,
  Space, Button, Popconfirm, Modal, Select, Tooltip,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  usePaymentsForWorkOrder,
  usePaymentOptions,
  useCreatePayment,
  useUpdatePayment,
  useDeletePayment,
  useIncassaPayment,
} from '@/hooks/usePayments'
import { usePermission } from '@/hooks/usePermission'
import PagamentoModal from './PagamentoModal'
import type {
  WorkOrderPayment,
  WorkOrderPaymentCreate,
  FormaPagamento,
} from '@/types/payments'

const { Text } = Typography

interface PagamentiPanelProps {
  workOrderId: number
  collapsible?: boolean
}

const formatEuro = (val: string | number | null | undefined): string => {
  if (val == null) return '—'
  return `${Number(val).toFixed(2)} €`
}

const formatDate = (val: string | null | undefined): string => {
  if (!val) return '—'
  return dayjs(val).format('DD/MM/YYYY')
}

const TIPO_COLORS: Record<string, string> = { acconto: 'purple', rata: 'blue', saldo: 'green' }
const TIPO_LABELS: Record<string, string> = { acconto: 'Acconto', rata: 'Rata', saldo: 'Saldo' }
const STATO_COLORS: Record<string, string> = { previsto: 'default', incassato: 'success' }
const STATO_LABELS: Record<string, string> = { previsto: 'Previsto', incassato: 'Incassato' }

interface StatItemProps {
  label: React.ReactNode
  value: string
  color?: string
}

const StatItem: React.FC<StatItemProps> = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
    <Text strong style={{ fontSize: 13, color }}>{value}</Text>
  </div>
)

const PagamentiPanel: React.FC<PagamentiPanelProps> = ({ workOrderId, collapsible = true }) => {
  const { token } = theme.useToken()
  const { data, isLoading } = usePaymentsForWorkOrder(workOrderId)
  const { data: options } = usePaymentOptions()

  const canCreate = usePermission('payments.create')
  const canEdit = usePermission('payments.edit')
  const canDelete = usePermission('payments.delete')

  const createPayment = useCreatePayment(workOrderId)
  const updatePayment = useUpdatePayment(workOrderId)
  const deletePayment = useDeletePayment(workOrderId)
  const incassaPayment = useIncassaPayment(workOrderId)

  const [pagamentoModalOpen, setPagamentoModalOpen] = useState(false)
  const [editingPayment, setEditingPayment] = useState<WorkOrderPayment | null>(null)

  const [incassaFormaOpen, setIncassaFormaOpen] = useState(false)
  const [incassaTargetId, setIncassaTargetId] = useState<number | null>(null)
  const [incassaSelectedForma, setIncassaSelectedForma] = useState<FormaPagamento | undefined>(undefined)

  const items = data?.items ?? []
  const rie = data?.riepilogo
  const importoScaduto = rie ? Number(rie.importo_scaduto) : 0
  const daPianificare = rie?.da_pianificare != null ? Number(rie.da_pianificare) : null
  const today = dayjs()

  // ── Collasso ──────────────────────────────────────────────────────────────────
  const [aperto, setAperto] = useState(false)
  const autoApertoRef = useRef(false)

  useEffect(() => {
    if (items.length > 0 && !autoApertoRef.current) {
      autoApertoRef.current = true
      setAperto(true)
    }
  }, [items.length])

  const corpoVisibile = collapsible === false || aperto

  const summaryTesto = items.length > 0 && rie
    ? `incassato ${formatEuro(rie.totale_incassato)} di ${formatEuro(rie.importo_dovuto)}`
    : 'nessuno registrato'

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleIncassa = (record: WorkOrderPayment) => {
    if (record.forma_pagamento) {
      incassaPayment.mutate({
        id: record.id,
        data: { data_pagamento: today.format('YYYY-MM-DD') },
      })
    } else {
      setIncassaTargetId(record.id)
      setIncassaSelectedForma(undefined)
      setIncassaFormaOpen(true)
    }
  }

  const handleIncassaConForma = async () => {
    if (!incassaTargetId) return
    try {
      await incassaPayment.mutateAsync({
        id: incassaTargetId,
        data: {
          data_pagamento: today.format('YYYY-MM-DD'),
          forma_pagamento: incassaSelectedForma ?? null,
        },
      })
      setIncassaFormaOpen(false)
      setIncassaTargetId(null)
    } catch {
      // errore già mostrato dall'hook
    }
  }

  const handleConfirmPagamento = async (data: WorkOrderPaymentCreate) => {
    try {
      if (editingPayment) {
        await updatePayment.mutateAsync({ id: editingPayment.id, data })
      } else {
        await createPayment.mutateAsync(data)
      }
      setPagamentoModalOpen(false)
      setEditingPayment(null)
    } catch {
      // errore già mostrato dall'hook
    }
  }

  // ── Colonne tabella ──────────────────────────────────────────────────────────

  const formeOpt = (options?.forme_pagamento ?? []).map(v => ({ value: v, label: v }))

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 80,
      render: (v: string) => (
        <Tag color={TIPO_COLORS[v] ?? 'default'} style={{ fontSize: 11 }}>
          {TIPO_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: 'Stato',
      dataIndex: 'stato',
      width: 90,
      render: (v: string) => (
        <Tag color={STATO_COLORS[v] ?? 'default'} style={{ fontSize: 11 }}>
          {STATO_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: 'Importo',
      dataIndex: 'importo',
      width: 100,
      render: (v: string | number) => (
        <Text strong style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatEuro(v)}</Text>
      ),
    },
    {
      title: 'Forma',
      dataIndex: 'forma_pagamento',
      width: 90,
      render: (v: string | null) => (
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{v ?? '—'}</Text>
      ),
    },
    {
      title: 'Data prevista',
      dataIndex: 'data_prevista',
      width: 110,
      render: (v: string | null) => (
        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(v)}</Text>
      ),
    },
    {
      title: 'Data pagamento',
      dataIndex: 'data_pagamento',
      width: 115,
      render: (v: string | null) => (
        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(v)}</Text>
      ),
    },
    {
      title: 'Pagatore',
      dataIndex: 'pagatore',
      width: 100,
      render: (v: string | null) => (
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{v ?? '—'}</Text>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      render: (v: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{v ?? ''}</Text>
      ),
    },
    {
      title: '',
      width: 130,
      render: (_: unknown, record: WorkOrderPayment) => (
        <Space size={4}>
          {canEdit && record.stato === 'previsto' && (
            <Button
              size="small"
              type="primary"
              ghost
              loading={incassaPayment.isPending}
              onClick={() => handleIncassa(record)}
            >
              Incassa
            </Button>
          )}
          {canEdit && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingPayment(record)
                setPagamentoModalOpen(true)
              }}
            />
          )}
          {canDelete && (
            <Popconfirm
              title="Eliminare questo pagamento?"
              okText="Sì"
              cancelText="No"
              onConfirm={() => deletePayment.mutate(record.id)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deletePayment.isPending}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // ── Render ───────────────────────────────────────────────────────────────────

  const cardExtra = (canCreate || collapsible) ? (
    <Space size={8}>
      {canCreate && (
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingPayment(null)
            setPagamentoModalOpen(true)
          }}
        >
          Registra pagamento
        </Button>
      )}
      {collapsible && (
        <Button
          type="text"
          size="small"
          icon={aperto ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setAperto(o => !o)}
        />
      )}
    </Space>
  ) : null

  return (
    <>
      <Card
        size="small"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ fontSize: 14 }}>Pagamenti</Text>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{summaryTesto}</Text>
          </span>
        }
        loading={isLoading}
        extra={cardExtra}
        styles={{ body: { display: corpoVisibile ? undefined : 'none' } }}
      >
        {rie && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            padding: '8px 12px',
            background: token.colorFillAlter,
            borderRadius: token.borderRadiusSM,
            marginBottom: 12,
          }}>
            <StatItem label="Dovuto" value={formatEuro(rie.importo_dovuto)} />
            <StatItem label="Imponibile" value={formatEuro(rie.totale_imponibile)} />
            <StatItem label="IVA" value={formatEuro(rie.totale_iva)} />
            <StatItem
              label={
                <Tooltip title="Somma dei pagamenti registrati, sia incassati sia previsti.">
                  <span style={{ cursor: 'default', borderBottom: '1px dashed currentColor' }}>
                    Totale registrato
                  </span>
                </Tooltip>
              }
              value={formatEuro(rie.totale_pianificato)}
              color={daPianificare !== null && daPianificare !== 0 ? token.colorWarning : undefined}
            />
            <StatItem
              label="Incassato"
              value={formatEuro(rie.totale_incassato)}
              color={token.colorSuccess}
            />
            <StatItem label="Da incassare" value={formatEuro(rie.totale_da_incassare)} />
            <StatItem
              label="Scaduto"
              value={formatEuro(rie.importo_scaduto)}
              color={importoScaduto > 0 ? token.colorWarning : undefined}
            />
            <StatItem
              label="Residuo"
              value={formatEuro(rie.residuo)}
              color={
                rie.residuo != null
                  ? (Number(rie.residuo) > 0 ? token.colorWarning : token.colorSuccess)
                  : undefined
              }
            />
            <StatItem label="Prossima scadenza" value={formatDate(rie.prossima_scadenza)} />
          </div>
        )}

        {items.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>Nessun pagamento registrato.</Text>
        ) : (
          <Table<WorkOrderPayment>
            dataSource={items}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            onRow={(record) => {
              const isScaduta =
                record.stato === 'previsto' &&
                !!record.data_prevista &&
                today.isAfter(dayjs(record.data_prevista), 'day')
              return {
                style: isScaduta ? { backgroundColor: token.colorWarningBg } : {},
              }
            }}
          />
        )}
      </Card>

      <PagamentoModal
        open={pagamentoModalOpen}
        onClose={() => {
          setPagamentoModalOpen(false)
          setEditingPayment(null)
        }}
        onConfirm={handleConfirmPagamento}
        loading={createPayment.isPending || updatePayment.isPending}
        payment={editingPayment ?? undefined}
        daPianificare={editingPayment ? undefined : daPianificare}
      />

      <Modal
        open={incassaFormaOpen}
        title="Forma di pagamento"
        okText="Incassa ora"
        cancelText="Annulla"
        onOk={handleIncassaConForma}
        onCancel={() => {
          setIncassaFormaOpen(false)
          setIncassaTargetId(null)
        }}
        confirmLoading={incassaPayment.isPending}
        width={340}
      >
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          Scegli la forma di pagamento per questo incasso:
        </div>
        <Select
          style={{ width: '100%' }}
          placeholder="Seleziona..."
          value={incassaSelectedForma}
          onChange={(v: FormaPagamento) => setIncassaSelectedForma(v)}
          options={formeOpt}
          allowClear
        />
      </Modal>
    </>
  )
}

export default PagamentiPanel
